import React, { forwardRef, useState, useContext } from "react";
import Modal from "../../components/Modal";
import Button from "../../components/Button";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { AppContext } from "../../context/app.context";
import { ModelCxContext } from "../../context/modelCx.context";
import { RESCHEDULE_REASON_OPTIONS } from "../../utility/keys";
import ReadOnlyShield from "../../components/ReadOnlyShield";

function SuppressModal({ currentJob, suppress, setOpenSuppressModal }) {
  const { whichList, isTimeTraveling } = useContext(AppContext);
  const { setSuppressionById: tsSetSuppressionById, tsUserDocument } = useContext(ModelCxContext);
  const [activePeriod, setActivePeriod] = useState(1);
  const todayDate = new Date();
  // initial date is tomorrow
  todayDate.setDate(todayDate.getDate() + 1);
  const [suppressDate, setSuppressDate] = useState(todayDate);
  const [notes, setNotes] = useState("");
  const [rescheduleReason, setRescheduleReason] = useState("");

  console.log("SuppressModal currentJob: ", currentJob);

  // - - - - - - - - - -

  const onSave = () => {
    // reminder: this button is only visible/usable when statusCode === 3
    const suppressedDate = new Date(suppressDate).toLocaleDateString("en-us");
    const newNotes = notes?.trim();
    if (!rescheduleReason) {
      alert("Reason is required");
    } else if (!newNotes) {
      alert("Lowe's IMS Note is required");
    } else {
      // Save Suppress
      console.log(
        "SuppressModal -> onSave -> date:",
        suppressDate?.toLocaleDateString("en-us"),
        " reason:",
        rescheduleReason,
        " custom:",
        newNotes
      );

      // update Mongo (changeStream will push status change back to this client)
      // Use TanStack directly (Realm EOL Jan 13, 2026)
      const newStatusCode = 3.1;
      const mongoUsername = tsUserDocument?.username || "unknown";
      const history = new Date().toString().concat(" - ").concat(mongoUsername).concat(", tab:").concat(whichList);
      tsSetSuppressionById.mutate({
        id: currentJob.mongoId,
        updateFields: {
          statusCode: newStatusCode,
          date_suppressed: suppressedDate,
          FileMakerSuppressNotes: newNotes,
          rescheduleReason: rescheduleReason,
          history: history,
          _mongoUsername: mongoUsername,
          // Clear schedule fields (for unscheduling assigned jobs; no-op for RTS jobs)
          date_scheduled: "",
          date_estimatedCompletion: "",
          installer: "",
          extraCellId: null,
          extraCellText: null,
        },
      });

      // Close modal window
      setOpenSuppressModal({ show: false });
    }
  };

  // - - - - - - - - - -

  const onSetActivePeriod = (period) => {
    setActivePeriod(period);
    if (period >= 1) {
      const todayDate = new Date();
      todayDate.setDate(todayDate.getDate() + period);
      setSuppressDate(todayDate);
    }
  };

  // - - - - - - - - - -

  const onSuppressDateChange = (date) => {
    setSuppressDate(date);
    setActivePeriod(-1);
  };

  // - - - - - - - - - -

  const EstCompleteInput = forwardRef(({ value, onClick, className }, ref) => (
    <Button
      color="clear"
      className={`${className}`}
      refInner={ref}
      onClick={onClick}
    >
      <div className="flex items-center">
        <span>{value}</span>
        <span className="ml-1 arrow arrow-down arrow-small"></span>
      </div>
    </Button>
  ));

  // - - - - - - - - - -

  return (
    <Modal onClose={() => setOpenSuppressModal({ show: false })}>
      <Modal.Header>
        Suppress Job# <span className="font-bold">{suppress?.projectNo}</span>
      </Modal.Header>
      <Modal.Content>
        <ReadOnlyShield>
        {suppress ? (
          <div className="flex flex-col gap-4">
            <div className="flex flex-wrap w-56">
              <div className="flex-1 basis-2/4">Invoice#:</div>
              <div className="flex-1 basis-2/4 font-bold">
                {suppress.invoiceNo}
              </div>
            </div>
            <div className="flex flex-wrap w-56">
              <div className="flex-1 basis-2/4">Classification:</div>
              <div className="flex-1 basis-2/4 font-bold">
                {suppress.classification}
              </div>
            </div>
            <div className="text-lg font-bold">Suppress for ...</div>
            <div className="flex h-6">
              <Button
                color="clear"
                className={`w-28 ${
                  activePeriod === 1
                    ? "bg-skin-button-accent"
                    : "bg-crew-surface-hard"
                } `}
                onClick={() => onSetActivePeriod(1)}
              >
                1 Day
              </Button>
              <Button
                color="clear"
                className={`w-28 ${
                  activePeriod === 3
                    ? "bg-skin-button-accent"
                    : "bg-crew-surface-hard"
                } border-l border-l-black/[.20]`}
                onClick={() => onSetActivePeriod(3)}
              >
                3 Day
              </Button>
              <Button
                color="clear"
                className={`w-28 ${
                  activePeriod === 5
                    ? "bg-skin-button-accent"
                    : "bg-crew-surface-hard"
                } border-l border-l-black/[.20]`}
                onClick={() => onSetActivePeriod(5)}
              >
                5 Day
              </Button>
              <DatePicker
                selected={suppressDate}
                onChange={(date) => onSuppressDateChange(date)}
                className=""
                customInput={
                  <EstCompleteInput
                    className={`w-28 h-6 ${
                      activePeriod === -1
                        ? "bg-skin-button-accent"
                        : "bg-crew-surface-hard"
                    } border-l border-l-black/[.20]`}
                  />
                }
                dateFormat="M/d/YYYY"
              />
            </div>
            <div className="text-lg font-bold">Reason</div>
            <select
              className="bg-yellow-300 text-black text-sm font-medium hover:opacity-75 cursor-pointer p-2 h-auto border border-black/[.20]"
              value={rescheduleReason}
              onChange={(e) => setRescheduleReason(e.target.value)}
            >
              <option value="">-- Select Reason --</option>
              {RESCHEDULE_REASON_OPTIONS.map((reason) => (
                <option key={reason} value={reason}>
                  {reason}
                </option>
              ))}
            </select>
            <div className="text-lg font-bold">Lowe's IMS Note</div>
            <textarea
              className="h-32 p-2 text-black border border-black/[.20]"
              placeholder="Custom"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            ></textarea>
          </div>
        ) : (
          "Suppress not found"
        )}
        </ReadOnlyShield>
      </Modal.Content>
      <Modal.Footer>
        <div className="h-8 flex gap-4">
          {/* Time Machine #114 Phase 4.5 — hide Save in read-only mode. */}
          {!isTimeTraveling ? (
            <Button className="uppercase w-31" onClick={() => onSave()}>
              Save
            </Button>
          ) : null}
          <Button
            color="light"
            className="uppercase w-31"
            onClick={() => setOpenSuppressModal({ show: false })}
          >
            {isTimeTraveling ? "Close" : "Cancel"}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
}

export default SuppressModal;
